/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMyProfile } from '@/features/myProfile/api/getMyProfile';
import type { EmailPromoState, PromoContent } from '../features/emailTab/interface';
import { useLabels } from '@/hooks/useLabels';
import { useSelector } from 'react-redux';
import type { ReduxState } from '@/types';
import { useRouter } from 'next/router';
import { routePaths } from '@/constants/routePaths';
import { localStorage } from '@/utils/localStorage';

type ModalOPPromptContextType = {
  emailPromptAlertType: boolean;
  isUserPending: boolean;
  promoState: EmailPromoState;
  updatePromoState: (
    updater: EmailPromoState | ((prev: EmailPromoState) => EmailPromoState)
  ) => void;
  shouldHideComponent: boolean;
  offer: PromoContent | null;
  promoPrint: boolean;
  optInValue: boolean;
};

const ModalOPPromptContext = createContext<ModalOPPromptContextType>(
  {} as ModalOPPromptContextType
);

const labelMap = {
  lblPromoDiscount1: 'label_email_promo_discount1',
  lblPromoDiscount2: 'label_email_promo_discount2',
  lblPromoDiscountText1: 'label_email_promo_discount_text1',
  lblPromoDiscountText2: 'label_email_promo_discount_text2',
  lblPromoValue1: 'label_email_promo_value1',
  lblPromoValue2: 'label_email_promo_value2',
  lblpromoCode1: 'label_email_promo_code1',
  lblpromoCode2: 'label_email_promo_code2',
  lblPromoExpirationDate1: 'label_promo_expiration_date1',
  lblPromoExpirationDate2: 'label_promo_expiration_date2',
};

export const useModalOPPromptContext = () => {
  const context = useContext(ModalOPPromptContext);
  if (context === undefined) {
    throw new Error('useModalOPPromptContext must be used within a ModalOPPromptProvider');
  }
  return context;
};

type ModalOPPromptProviderProps = {
  children: ReactNode;
};

export const ModalOPPromptProvider = ({ children }: ModalOPPromptProviderProps) => {
  const [promoState, setPromoState] = useState<EmailPromoState>({
    isOpen: false,
    isClosed: false,
    isConfirmed: false,
  });
  const [offer, setOffer] = useState<PromoContent | null>(null);
  const [showSideTab, setShowSideTab] = useState(false);

  const { data: currentUser, isPending: isUserPending } = useMyProfile();
  const labels = useLabels(labelMap);
  const router = useRouter();
  const userAuthenticated = useSelector(({ appData }: ReduxState) => appData.userAuthenticated);

  const promoPrint = currentUser?.receivePromoPrint === '1';
  const optInValue = Boolean(currentUser?.receivePromoAndSpecialEvents);
  const emailPromptAlertType = useMemo((): boolean => {
    if (!currentUser || !currentUser.loyaltyAccountAlerts) {
      return false;
    }

    const loyaltyAccountAlertPromptObject = currentUser.loyaltyAccountAlerts.find(
      (alert) => alert.alertType === 'OP'
    );

    return loyaltyAccountAlertPromptObject?.status === 'PROMPT' || false;
  }, [currentUser]);

  useEffect(() => {
    // For the users that have receivePromoAndSpecialEvents = false
    if (optInValue === false && !showSideTab && userAuthenticated) {
      // if user has 'OP' alert, show first promo ($10 off $50)
      if (emailPromptAlertType) {
        setOffer({
          discount: labels.lblPromoDiscount1,
          discountText: labels.lblPromoDiscountText1,
          value: labels.lblPromoValue1,
          promoCode: labels.lblpromoCode1,
          expirationDate: labels.lblPromoExpirationDate1,
        });
      }

      // if user don't have any alert, show second promo ($20 off $100)
      if (!emailPromptAlertType) {
        setOffer({
          discount: labels.lblPromoDiscount2,
          discountText: labels.lblPromoDiscountText2,
          value: labels.lblPromoValue2,
          promoCode: labels.lblpromoCode2,
          expirationDate: labels.lblPromoExpirationDate2,
        });
      }
    } else if (!userAuthenticated && showSideTab) {
      setShowSideTab(false);
      setOffer(null);
      setPromoState({
        isOpen: false,
        isClosed: false,
        isConfirmed: false,
      });
    }
  }, [optInValue, labels, emailPromptAlertType, showSideTab, userAuthenticated]);

  useEffect(() => {
    const displayAlert = localStorage.getItem('displayEmailOptInAlert') !== 'false';

    const shouldShow =
      userAuthenticated &&
      displayAlert &&
      !promoState.isClosed &&
      !isUserPending &&
      !(optInValue === true && !promoState.isConfirmed) &&
      offer !== null;

    if (shouldShow) {
      setShowSideTab(true);
    }
  }, [promoState, isUserPending, emailPromptAlertType, optInValue, offer, userAuthenticated]);

  const updatePromoState = useCallback(
    (updater: EmailPromoState | ((prev: EmailPromoState) => EmailPromoState)) => {
      setPromoState(updater);
    },
    []
  );

  const shouldHideComponent = useMemo(() => {
    if (!userAuthenticated) {
      return true;
    }
    if (promoState.isClosed) {
      return true;
    }

    const prohibitedRoutes = [routePaths.checkout, routePaths.cart];
    const isProhibitedRoute = prohibitedRoutes.some((route) => router.asPath.includes(route));

    if (isProhibitedRoute) {
      return true;
    }

    if (showSideTab) {
      return false;
    }
    return true;
  }, [userAuthenticated, promoState.isClosed, router.asPath, showSideTab]);

  return (
    <ModalOPPromptContext.Provider
      value={{
        emailPromptAlertType,
        isUserPending,
        promoState,
        updatePromoState,
        shouldHideComponent,
        offer,
        promoPrint,
        optInValue,
      }}
    >
      {children}
    </ModalOPPromptContext.Provider>
  );
};
